<template>
  <div id="farmMenu">
    <!-- FARM MENU-->
    <div id="farmTopMenu">
      <b-btn
        @click="$store.commit('showDrawer', 'farmDetails')"
        variant="success"
        id="farmButton"
      ><i class="fas fa-farm"></i><span id="farmName"> {{ $store.state.farm.name }}</span></b-btn>
      <!-- LIVE pill — vehicles tracking right now (docs/live-tracking-phase1.md).
           Click enables the Live Activity layer for this session and fits the
           map to the live tracks (the one user-initiated camera move). -->
      <b-btn
        v-if="$store.state.liveTrackCount > 0"
        @click="$store.dispatch('focusLiveTracks')"
        variant="danger"
        id="livePill"
        v-b-tooltip.hover.bottom="'Vehicles tracking now — click to view'"
      ><span class="live-dot"></span> LIVE</b-btn>
    </div><!-- /#farmTopMenu -->

    <!-- REFRESH FARM-->
    <b-btn
      v-if="!hideMapTogglesForCollector"
      v-b-tooltip.hover.bottom="'Refresh Farm'"
      @click="$store.dispatch('refreshFarm')"
      class="map-toggle ml-1"
      id="refreshFarm"
    ><i class="far fa-redo-alt"></i></b-btn>

    <!-- Toggle in datasets button -->
    <b-btn
      id="datasetsToggle"
      @click="showDrawer('datasets')"
      class="map-toggle"
      v-b-tooltip.hover.right="'Datasets'"
    >
      <i class="fal fa-database"></i>
    </b-btn>

    <!-- Toggle in plugins button -->
    <b-btn
      id="blockPluginsToggle"
      @click="showDrawer('plugins')"
      class="map-toggle hide-mobile"
      v-b-tooltip.hover.right="'Plugins'"
      size="sm"
      v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor'"
    >
      <i class="fas fa-flask-potion"></i>
    </b-btn>

    <!-- Toggle in user details button -->
    <b-btn
      id="userDetailsToggle"
      @click="showDrawer('userDetails')"
      class="map-toggle"
      v-b-tooltip.hover.right="'User Settings'"
      size="sm"
    >
      <i class="fas fa-user"></i>
    </b-btn>

    <!-- Toggle satellite imagery panel -->
    <b-btn
      id="satelliteToggle"
      @click="showDrawer('satellite')"
      class="map-toggle"
      v-b-tooltip.hover.left="'Satellite Imagery'"
      variant="primary"
    >
      <i class="fas fa-satellite"></i>
    </b-btn>

    <!-- Toggle in collectors button -->
    <b-btn
      id="collectorsToggle"
      @click="showDrawer('collectors')"
      class="map-toggle"
      v-b-tooltip.hover.left="'Collect Data'"
      variant="primary"
    >
      <i class="far fa-clipboard-check"></i>
    </b-btn>

    <!-- Toggle Terrain -->
    <b-btn
      v-if="!hideMapTogglesForCollector"
      id="terrainToggle"
      class="map-toggle"
      @click="toggleTerrain()"
      v-b-tooltip.hover.right="'Toggle Terrain Mode'"
      size="sm"
    >
      <span v-if="!terrainVisible">3D</span><span v-if="terrainVisible">2D</span>
    </b-btn>

  </div><!-- /#farmMenu -->
</template>

<script>
export default {
  data () {
    return {
      terrainVisible: false
    }
  },
  created () {
    // Count-only live-tracking listener for the pill; cheap (no lines reads).
    this.$store.dispatch('subscribeLiveTrackCount')
  },
  watch: {
    '$store.state.farm.id' () {
      this.$store.dispatch('subscribeLiveTrackCount')
    },
    // Deep link from the Missions plugin ("View live"): /f/:id?live=1 focuses
    // the live tracks once the pill knows there are any AND the farm's own
    // initial camera move has happened (loadingMap → false), so it isn't
    // overridden; then drops the query so a refresh behaves normally.
    '$store.state.liveTrackCount': { immediate: true, handler: 'maybeFocusLiveDeepLink' },
    '$store.state.loadingMap': { immediate: true, handler: 'maybeFocusLiveDeepLink' }
  },
  beforeDestroy () {
    this.$store.dispatch('unsubscribeLiveTrackCount')
  },
  computed: {
    hideMapTogglesForCollector () {
      const c = this.$store.state.collector
      const drawingGeometry = c && c.collecting && (c.geometryType === 'polygon' || c.geometryType === 'line')
      const otherDrawers = this.$store.state.drawer.createBlockDrawer.show ||
        this.$store.state.drawer.createFarm.show ||
        this.$store.state.drawer.createDataset.show
      return drawingGeometry || otherDrawers
    }
  },
  methods: {
    maybeFocusLiveDeepLink () {
      const st = this.$store.state
      if (!this.$route.query.live || !st.liveTrackCount || st.loadingMap) return
      const query = Object.assign({}, this.$route.query)
      delete query.live
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
      // Let the farm's own fitBounds settle before flying to the tracks.
      setTimeout(() => this.$store.dispatch('focusLiveTracks'), 600)
    },
    createBlock () {
      this.$store.commit('setEditMode', false)
      this.$store.commit('hideAllDrawers')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'createBlockDrawer')
      }, 300)
    },
    editFarm (id) {
      this.$store.commit('setEditMode', true)
      this.$store.commit('toggleModal', 'createFarm')
    },
    goToFarm (id) {
      window.location.href = '/f/' + id
    },
    showDrawer (drawer) {
      this.$store.commit('hideAllDrawers')
      setTimeout(() => {
        this.$store.commit('showDrawer', drawer)
      }, 300)
    },
    loadTerrain () {
      this.terrainVisible = true
      const map = this.$store.state.mapObject
      map.setTerrain(null)
      if (map.getLayer('sky')) {
        map.removeLayer('sky')
      }
      if (map.getSource('mapbox-dem')) {
        map.removeSource('mapbox-dem')
      }
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1'
      })
      map.addLayer({
        'id': 'sky', // eslint-disable-line
        'type': 'sky', // eslint-disable-line
        'paint': { // eslint-disable-line
          'sky-type': 'atmosphere', // eslint-disable-line
          'sky-atmosphere-sun': [0.0, 0.0], // eslint-disable-line
          'sky-atmosphere-sun-intensity': 15 // eslint-disable-line
        }
      })
      map.setTerrain({ source: 'mapbox-dem' })
      map.flyTo({
        pitch: 70
      })
    },
    removeTerrain () {
      this.terrainVisible = false
      const map = this.$store.state.mapObject
      map.setTerrain(null)
      if (map.getLayer('sky')) {
        map.removeLayer('sky')
      }
      if (map.getSource('mapbox-dem')) {
        map.removeSource('mapbox-dem')
      }
      map.flyTo({
        pitch: 0,
        bearing: 0
      })
    },
    toggleTerrain () {
      if (this.terrainVisible) {
        this.removeTerrain()
      } else {
        this.loadTerrain()
      }
    }
  }
}
</script>

<style scoped>
#livePill {
  margin-left: 6px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.live-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
  animation: livePulse 1.4s ease-in-out infinite;
}
@keyframes livePulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
@media (prefers-reduced-motion: reduce) {
  .live-dot { animation: none; }
}
#refreshFarm {
  position: fixed;
  right: 10px;
  top: 165px;
}

#datasetsToggle {
  position: fixed;
  left: 10px;
  top: 60px;
}
#farmTopMenu {
  position: fixed;
  z-index: 2;
  left: 10px;
  top: 10px;
}
#userDetailsToggle {
  position: fixed;
  z-index: 1;
  left: 10px;
  bottom: 70px;
}
#collectorsToggle {
  position: fixed;
  z-index: 1;
  right: 10px;
  bottom: 50px;
}

#satelliteToggle {
  position: fixed;
  z-index: 1;
  right: 10px;
  bottom: 104px;
}

#terrainToggle {
  position: fixed;
  z-index: 1;
  top: 115px;
  right: 10px;
}

#blockPluginsToggle {
  position: fixed;
  z-index: 1;
  left: 10px;
  top: 110px;
}

@media(max-width:600px) {
  #farmName {
    display: none;
  }
  #farmButton {
    width: 44px;
    height: 44px;
  }
  #refreshFarm {
    position: fixed;
    right: 10px;
    top: 65px;
  }
  #terrainToggle {
    display: none;
  }
}
</style>
